'use strict';

const libPictProvider = require('pict-provider');

/**
 * AssetCatalogSyncProvider — the engine behind the #/AssetCatalogSync screen.
 *
 * Seeds THIS customer's PhysicalAsset* catalog from the shared public data lake (the AASHTO/NBE national
 * standard, reachable through the main /1.0/ proxy). The public lake is the shared master; each customer gets
 * its own copy so they can adjust it with local nuance — so this sync is ADDITIVE (create-missing only): it
 * NEVER overwrites a row the customer already has, matching on the natural key (Code within the customer).
 * Re-running is therefore idempotent and safe to run against a catalog an admin has already tuned.
 *
 * Mapping (lake -> per-customer catalog):
 *   AssetMaterial                        -> PhysicalAssetMaterial       (Code, Material, Description)
 *   Element                              -> PhysicalAssetElementType    (Code, Section, SectionCode, Name, Unit,
 *                                           Clarification, IDPhysicalAssetMaterial resolved via the material Code)
 *   BridgeElementConditionDefect         -> PhysicalAssetDefect         (Code, Name, Description, Notes,
 *                                           PrimaryDocumentationURL; CS1-CS4 packed into StateDescriptionsJSON)
 *   BridgeElementConditionDefectElementJoin -> PhysicalAssetElementTypePhysicalAssetDefectJoin
 *                                           (which defects apply to which element type)
 *
 * IDCustomer is server-stamped from the session — never sent. Row GUIDs auto-generate (we do NOT carry the
 * lake BRM-* GUID across, to avoid the soft-deleted-GUID-uniqueness trap on re-sync; the Code is the key).
 *
 * State lives in pict.AppData.AssetCatalogSync; the (large) lake payloads are held on the instance.
 */

const PAGE = 500;            // read page size for the /1.0/{Set}/{start}/{cap} paging
const WRITE_BATCH = 200;     // bulk-create chunk size (POST /1.0/{Set} with an array)

// Lake reference sets and the customer catalog sets they seed.
const LAKE = { Material: 'AssetMaterials', Element: 'Elements', Defect: 'BridgeElementConditionDefects', Join: 'BridgeElementConditionDefectElementJoins' };
const CAT  = { Material: 'PhysicalAssetMaterials', Element: 'PhysicalAssetElementTypes', Defect: 'PhysicalAssetDefects', Join: 'PhysicalAssetElementTypePhysicalAssetDefectJoins' };

class AssetCatalogSyncProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		// Held off-state (they are large): the lake reference payloads + the last computed plan.
		this._lake = null;
		this._plan = null;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get currentUser() { return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {}; }
	get idCustomer() { return this.currentUser.CustomerID; }
	get customerName() { return this.currentUser.CustomerName || this.currentUser.Customer || ('Customer ' + this.idCustomer); }

	get isSuperUser()
	{
		const tmpUser = this.currentUser;
		return (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1);
	}

	get hasCustomerAdmin()
	{
		const tmpRoles = this.currentUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'CustomerAdmin' || pRole.PermissionLevel === 'SuperAdminUser'));
	}

	/** Who may run the sync (writes to the catalog). UI gate only — the Meadow role auth is authoritative. */
	get canEdit() { return this.isSuperUser || this.hasCustomerAdmin; }

	get state()
	{
		if (!this.pict.AppData.AssetCatalogSync)
		{
			this.pict.AppData.AssetCatalogSync =
			{
				Loading: false, Loaded: false, Error: '',
				Plan: null,                 // { Material:{lake,existing,toCreate}, Element:{…}, Defect:{…}, Join:{lake} }
				Confirming: false,
				Applying: false, Progress: '', Result: null,
			};
		}
		return this.pict.AppData.AssetCatalogSync;
	}

	// --- REST helpers (promisified over the shared HeadlightRestClient) ---

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }
	_postJSON(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }

	/** Page through a Meadow set (returns every row). */
	async _readAll(pSet)
	{
		let tmpOut = [];
		let tmpStart = 0;
		for (;;)
		{
			const tmpRows = await this._getJSON(`${pSet}/${tmpStart}/${PAGE}`);
			if (!Array.isArray(tmpRows) || tmpRows.length === 0) { break; }
			tmpOut = tmpOut.concat(tmpRows);
			if (tmpRows.length < PAGE) { break; }
			tmpStart += PAGE;
			if (tmpStart > 100000) { break; } // safety
		}
		return tmpOut;
	}

	/** Bulk-create rows into a set (chunked). Returns the count actually POSTed. */
	async _bulkCreate(pSet, pRows)
	{
		let tmpCount = 0;
		for (let i = 0; i < pRows.length; i += WRITE_BATCH)
		{
			const tmpChunk = pRows.slice(i, i + WRITE_BATCH);
			const tmpResult = await this._postJSON(pSet, tmpChunk);
			// Meadow bulk create surfaces an in-body error object on failure — treat that as fatal for this stage.
			if (tmpResult && tmpResult.Error) { throw new Error(`${pSet}: ${tmpResult.Error}`); }
			tmpCount += tmpChunk.length;
			this._progress(`Created ${tmpCount} / ${pRows.length} into ${pSet}…`);
		}
		return tmpCount;
	}

	static _index(pRows, pKey)
	{
		const tmpMap = {};
		(pRows || []).forEach((pRow) => { if (pRow && (pRow[pKey] !== undefined) && (pRow[pKey] !== null)) { tmpMap[String(pRow[pKey])] = pRow; } });
		return tmpMap;
	}

	// --- Plan (dry-run preview; NO writes) ---

	/**
	 * Read the lake reference catalog + this customer's existing catalog, and compute what an additive sync
	 * would create. Leaves the lake payloads on the instance for a subsequent apply.
	 */
	async loadPlanAsync()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return; }
		tmpState.Loading = true; tmpState.Error = ''; tmpState.Result = null; tmpState.Confirming = false;
		this.render();
		try
		{
			// Shared lake reference (the AASHTO master).
			const [ tmpLakeMat, tmpLakeElem, tmpLakeDef, tmpLakeJoin ] = await Promise.all(
			[
				this._readAll(LAKE.Material), this._readAll(LAKE.Element), this._readAll(LAKE.Defect), this._readAll(LAKE.Join),
			]);
			// This customer's existing catalog (live rows only).
			const [ tmpCurMat, tmpCurElem, tmpCurDef ] = await Promise.all(
			[
				this._readAll(`${CAT.Material}/FilteredTo/FBV~Deleted~EQ~0`),
				this._readAll(`${CAT.Element}/FilteredTo/FBV~Deleted~EQ~0`),
				this._readAll(`${CAT.Defect}/FilteredTo/FBV~Deleted~EQ~0`),
			]);

			this._lake = { Material: tmpLakeMat, Element: tmpLakeElem, Defect: tmpLakeDef, Join: tmpLakeJoin };

			const tmpMatByCode = AssetCatalogSyncProvider._index(tmpCurMat, 'Code');
			const tmpElemByCode = AssetCatalogSyncProvider._index(tmpCurElem, 'Code');
			const tmpDefByCode = AssetCatalogSyncProvider._index(tmpCurDef, 'Code');

			const tmpMatToCreate = tmpLakeMat.filter((pRow) => !tmpMatByCode[String(pRow.Code)]);
			const tmpElemToCreate = tmpLakeElem.filter((pRow) => !tmpElemByCode[String(pRow.Code)]);
			const tmpDefToCreate = tmpLakeDef.filter((pRow) => !tmpDefByCode[String(pRow.Code)]);

			this._plan =
			{
				Material: { lake: tmpLakeMat.length, existing: tmpCurMat.length, toCreate: tmpMatToCreate },
				Element:  { lake: tmpLakeElem.length, existing: tmpCurElem.length, toCreate: tmpElemToCreate },
				Defect:   { lake: tmpLakeDef.length, existing: tmpCurDef.length, toCreate: tmpDefToCreate },
				Join:     { lake: tmpLakeJoin.length }, // per-row create resolved at apply (needs new element/defect IDs)
			};
			tmpState.Plan =
			{
				Material: { lake: tmpLakeMat.length, existing: tmpCurMat.length, toCreate: tmpMatToCreate.length },
				Element:  { lake: tmpLakeElem.length, existing: tmpCurElem.length, toCreate: tmpElemToCreate.length },
				Defect:   { lake: tmpLakeDef.length, existing: tmpCurDef.length, toCreate: tmpDefToCreate.length },
				Join:     { lake: tmpLakeJoin.length },
			};
			tmpState.Loaded = true;
		}
		catch (pError)
		{
			tmpState.Error = (pError && pError.message) || String(pError);
		}
		finally
		{
			tmpState.Loading = false;
			this.render();
		}
	}

	// --- Apply (writes; additive create-missing) ---

	askApply() { this.state.Confirming = true; this.render(); }
	cancelApply() { this.state.Confirming = false; this.render(); }

	_progress(pMessage) { this.state.Progress = pMessage; this.render(); }

	/**
	 * Execute the additive sync. Dependency order: materials -> element types (need material ids) ->
	 * defects -> element-type/defect joins (need both new- side ids). After each create stage we RE-READ the
	 * customer set to resolve Code -> new id (robust regardless of the bulk-create response shape).
	 */
	async applyAsync()
	{
		const tmpState = this.state;
		if (tmpState.Applying) { return; }
		if (!this.canEdit) { tmpState.Error = 'You do not have permission to write the asset catalog.'; this.render(); return; }
		if (!this._plan) { await this.loadPlanAsync(); if (this.state.Error) { return; } }

		tmpState.Applying = true; tmpState.Confirming = false; tmpState.Error = ''; tmpState.Result = null;
		const tmpResult = { Material: 0, Element: 0, Defect: 0, Join: 0 };
		this._progress('Starting…');
		try
		{
			// 1) Materials.
			if (this._plan.Material.toCreate.length)
			{
				const tmpRows = this._plan.Material.toCreate.map((pRow) => ({ Code: pRow.Code, Material: pRow.Material, Description: pRow.Description || '' }));
				tmpResult.Material = await this._bulkCreate(CAT.Material, tmpRows);
			}
			// Resolve material maps (existing + just-created).
			const tmpCurMat = await this._readAll(`${CAT.Material}/FilteredTo/FBV~Deleted~EQ~0`);
			const tmpMatByCode = AssetCatalogSyncProvider._index(tmpCurMat, 'Code');
			const tmpLakeMatById = AssetCatalogSyncProvider._index(this._lake.Material, 'IDAssetMaterial');

			// 2) Element types (resolve IDPhysicalAssetMaterial via the lake material Code).
			if (this._plan.Element.toCreate.length)
			{
				const tmpRows = this._plan.Element.toCreate.map((pRow) =>
				{
					const tmpLakeMat = tmpLakeMatById[String(pRow.IDAssetMaterial)];
					const tmpCustMat = tmpLakeMat ? tmpMatByCode[String(tmpLakeMat.Code)] : null;
					return {
						Code: pRow.Code, Section: pRow.Section, SectionCode: pRow.SectionCode, Name: pRow.Name,
						Unit: pRow.Unit, Clarification: pRow.Clarification || '',
						IDPhysicalAssetMaterial: tmpCustMat ? tmpCustMat.IDPhysicalAssetMaterial : 0,
					};
				});
				tmpResult.Element = await this._bulkCreate(CAT.Element, tmpRows);
			}

			// 3) Defects (pack the four condition-state descriptions into StateDescriptionsJSON).
			if (this._plan.Defect.toCreate.length)
			{
				const tmpRows = this._plan.Defect.toCreate.map((pRow) => (
				{
					Code: pRow.Code, Name: pRow.Name, Description: pRow.Description || '', Notes: pRow.Notes || '',
					PrimaryDocumentationURL: pRow.PrimaryDocumentationURL || '',
					StateDescriptionsJSON: JSON.stringify({ CS1: pRow.CS1Description || '', CS2: pRow.CS2Description || '', CS3: pRow.CS3Description || '', CS4: pRow.CS4Description || '' }),
				}));
				tmpResult.Defect = await this._bulkCreate(CAT.Defect, tmpRows);
			}

			// 4) Element-type <-> defect joins. Re-read both sides to map Code -> new id, and existing joins to skip.
			this._progress('Linking defects to element types…');
			const [ tmpCurElem2, tmpCurDef2, tmpCurJoin ] = await Promise.all(
			[
				this._readAll(`${CAT.Element}/FilteredTo/FBV~Deleted~EQ~0`),
				this._readAll(`${CAT.Defect}/FilteredTo/FBV~Deleted~EQ~0`),
				this._readAll(`${CAT.Join}/FilteredTo/FBV~Deleted~EQ~0`),
			]);
			const tmpElemByCode = AssetCatalogSyncProvider._index(tmpCurElem2, 'Code');
			const tmpDefByCode = AssetCatalogSyncProvider._index(tmpCurDef2, 'Code');
			const tmpLakeElemById = AssetCatalogSyncProvider._index(this._lake.Element, 'IDElement');
			const tmpLakeDefById = AssetCatalogSyncProvider._index(this._lake.Defect, 'IDBridgeElementConditionDefect');
			const tmpJoinSig = {};
			tmpCurJoin.forEach((pRow) => { tmpJoinSig[`${pRow.IDPhysicalAssetElementType}|${pRow.IDPhysicalAssetDefect}`] = true; });

			const tmpJoinRows = [];
			this._lake.Join.forEach((pRow) =>
			{
				const tmpLakeElem = tmpLakeElemById[String(pRow.IDElement)];
				const tmpLakeDef = tmpLakeDefById[String(pRow.IDBridgeElementConditionDefect)];
				if (!tmpLakeElem || !tmpLakeDef) { return; }
				const tmpElem = tmpElemByCode[String(tmpLakeElem.Code)];
				const tmpDef = tmpDefByCode[String(tmpLakeDef.Code)];
				if (!tmpElem || !tmpDef) { return; }
				const tmpSig = `${tmpElem.IDPhysicalAssetElementType}|${tmpDef.IDPhysicalAssetDefect}`;
				if (tmpJoinSig[tmpSig]) { return; }
				tmpJoinSig[tmpSig] = true; // guard against dupes within this batch
				tmpJoinRows.push({ IDPhysicalAssetElementType: tmpElem.IDPhysicalAssetElementType, IDPhysicalAssetDefect: tmpDef.IDPhysicalAssetDefect });
			});
			if (tmpJoinRows.length) { tmpResult.Join = await this._bulkCreate(CAT.Join, tmpJoinRows); }

			tmpState.Result = tmpResult;
			tmpState.Progress = '';
			// Refresh the plan so the table now shows everything present (toCreate -> 0).
			await this.loadPlanAsync();
			this.state.Result = tmpResult;
		}
		catch (pError)
		{
			tmpState.Error = (pError && pError.message) || String(pError);
		}
		finally
		{
			this.state.Applying = false;
			this.render();
		}
	}

	// --- Render (assigns the body HTML into the view's mount) ---

	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
	_n(pValue) { const tmpN = Number(pValue); return Number.isNaN(tmpN) ? '—' : tmpN.toLocaleString('en-US'); }

	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || tmpCA.getElement('#AssetCatalogSync-Body').length === 0) { return; }
		tmpCA.assignContent('#AssetCatalogSync-Body', this._bodyHTML());
	}

	_planRowHTML(pLabel, pIcon, pRow)
	{
		if (!pRow) { return ''; }
		const tmpToCreate = (pRow.toCreate === undefined) ? null : pRow.toCreate;
		const tmpBadge = (tmpToCreate === null)
			? `<span class="acs-badge acs-badge-info">${this._n(pRow.lake)} available</span>`
			: (tmpToCreate > 0 ? `<span class="acs-badge acs-badge-new">+${this._n(tmpToCreate)} new</span>` : `<span class="acs-badge acs-badge-ok">up to date</span>`);
		const tmpExisting = (pRow.existing === undefined) ? '—' : this._n(pRow.existing);
		return `<tr>
			<td class="acs-cell-label"><i class="${pIcon}"></i> ${this._esc(pLabel)}</td>
			<td class="acs-num">${this._n(pRow.lake)}</td>
			<td class="acs-num">${tmpExisting}</td>
			<td class="acs-num">${tmpToCreate === null ? '—' : this._n(tmpToCreate)}</td>
			<td>${tmpBadge}</td>
		</tr>`;
	}

	_bodyHTML()
	{
		const tmpState = this.state;
		if (tmpState.Loading && !tmpState.Plan) { return `<div class="acs-note"><i class="fas fa-circle-notch fa-spin"></i> Reading the public lake and your catalog…</div>`; }
		if (tmpState.Error) { return `<div class="acs-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpState.Error)}</div>${this._actionsHTML()}`; }
		if (!tmpState.Plan) { return `<div class="acs-note">Preparing…</div>`; }

		const tmpPlan = tmpState.Plan;
		const tmpTotalNew = tmpPlan.Material.toCreate + tmpPlan.Element.toCreate + tmpPlan.Defect.toCreate;

		const tmpTable = `<table class="acs-table">
			<thead><tr><th>Catalog</th><th class="acs-num">In lake</th><th class="acs-num">You have</th><th class="acs-num">New</th><th></th></tr></thead>
			<tbody>
				${this._planRowHTML('Asset Materials', 'fas fa-layer-group', tmpPlan.Material)}
				${this._planRowHTML('Element Types', 'fas fa-diagram-project', tmpPlan.Element)}
				${this._planRowHTML('Defects', 'fas fa-triangle-exclamation', tmpPlan.Defect)}
				${this._planRowHTML('Element ⇄ Defect links', 'fas fa-link', tmpPlan.Join)}
			</tbody>
		</table>`;

		let tmpResultHTML = '';
		if (tmpState.Result)
		{
			const tmpR = tmpState.Result;
			tmpResultHTML = `<div class="acs-result"><i class="fas fa-circle-check"></i> Sync complete — created
				<b>${this._n(tmpR.Material)}</b> materials, <b>${this._n(tmpR.Element)}</b> element types,
				<b>${this._n(tmpR.Defect)}</b> defects, <b>${this._n(tmpR.Join)}</b> links.</div>`;
		}

		const tmpSummary = tmpTotalNew > 0
			? `<p class="acs-summary">This will <b>add ${this._n(tmpTotalNew)}</b> catalog rows to <b>${this._esc(this.customerName)}</b> (plus element⇄defect links). Existing rows you have already tuned are left untouched.</p>`
			: `<p class="acs-summary acs-summary-ok">${this._esc(this.customerName)}’s catalog already matches the lake baseline. Nothing new to add.</p>`;

		return tmpSummary + tmpTable + tmpResultHTML + this._actionsHTML();
	}

	_actionsHTML()
	{
		const tmpState = this.state;
		if (tmpState.Applying) { return `<div class="acs-actions"><span class="acs-progress"><i class="fas fa-circle-notch fa-spin"></i> ${this._esc(tmpState.Progress || 'Working…')}</span></div>`; }

		const tmpReload = `<button class="acs-btn acs-btn-ghost" onclick="_Pict.providers.AssetCatalogSync.loadPlanAsync()"${tmpState.Loading ? ' disabled' : ''}><i class="fas fa-rotate"></i> Reload plan</button>`;

		if (!this.canEdit)
		{
			return `<div class="acs-actions">${tmpReload}<span class="acs-note-inline">Running the sync needs a super-user or customer-admin.</span></div>`;
		}

		const tmpHasWork = tmpState.Plan && (tmpState.Plan.Material.toCreate + tmpState.Plan.Element.toCreate + tmpState.Plan.Defect.toCreate) > 0;
		if (tmpState.Confirming)
		{
			return `<div class="acs-actions acs-confirm">
				<span class="acs-confirm-q">Add the missing AASHTO catalog rows to ${this._esc(this.customerName)}?</span>
				<button class="acs-btn acs-btn-primary" onclick="_Pict.providers.AssetCatalogSync.applyAsync()"><i class="fas fa-cloud-arrow-down"></i> Yes, sync now</button>
				<button class="acs-btn acs-btn-ghost" onclick="_Pict.providers.AssetCatalogSync.cancelApply()">Cancel</button>
			</div>`;
		}
		const tmpApply = tmpHasWork
			? `<button class="acs-btn acs-btn-primary" onclick="_Pict.providers.AssetCatalogSync.askApply()"><i class="fas fa-cloud-arrow-down"></i> Sync from public lake</button>`
			: `<button class="acs-btn acs-btn-primary" disabled><i class="fas fa-check"></i> Up to date</button>`;
		return `<div class="acs-actions">${tmpApply}${tmpReload}</div>`;
	}
}

module.exports = AssetCatalogSyncProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'AssetCatalogSync',
	AutoInitialize: true,
	AutoInitializeOrdinal: 30,
};
